<template>
  <div class="stats-wrap">
    <div class="stats-frame">
    <el-table
      :data="tableRows"
      class="stats-table"
      :row-class-name="rowClassName"
      border
    >
      <el-table-column label="分类（周累计）" width="160" align="left">
        <template slot-scope="scope">
          <!-- 分类列统一「色点 + 文字」：名称字段里可能带图标前缀（'fa:book学习' / '🍕吃饭'），
               展示时把前缀剥掉只留纯文本，色点用分类自己的颜色，不再渲染图标 -->
          <span v-if="scope.row.color" class="dot" :style="{ background: scope.row.color }"></span>
          <span :class="{ 'total-label': scope.row.isTotal }">{{ scope.row.label }}</span>
          <span v-if="scope.row.weekTotal > 0" class="week-total" :class="{ 'total-cell': scope.row.isTotal }">（{{ formatDuration(scope.row.weekTotal) }}）</span>
        </template>
      </el-table-column>

      <el-table-column
        v-for="d in 7"
        :key="d"
        :label="dayLabels[d - 1]"
        align="center"
      >
        <template slot-scope="scope">
          <span :class="{ 'total-cell': scope.row.isTotal }">
            {{ formatDuration(scope.row.values[d - 1]) }}
          </span>
        </template>
      </el-table-column>

    </el-table>
    </div>

    <div class="summary">
      <span>共 <b>{{ formatDuration(weekTotal) }}</b> 已记录 / 168 小时</span>
    </div>
  </div>
</template>

<script>
import { DAY_NAMES, parseTagName } from './constants'

/**
 * 未分类记录的兜底分类 key。
 * 后端返回的时间块若没有 tagId，ListPage 会把它标成 'none'；
 * 分类被删除、或本地缓存里残留的历史 key，也一并归到这里。
 */
const UNCATEGORIZED_KEY = 'none'

export default {
  name: 'TimeStats',
  props: {
    days: {
      type: Array,
      required: true
    },
    events: {
      type: Array,
      default: () => [[], [], [], [], [], [], []]
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    displayCats() {
      return this.categories.filter(c => c.key !== 'none')
    },
    dayLabels() {
      return DAY_NAMES
    },
    /**
     * 逐日、逐分类的时长（小时）。
     *
     * 两个容易踩的坑：
     * 1) 时长不能直接写 `(evt.end - evt.start) / 60` —— 跨午夜的记录 end 会小于 start，
     *    算出来是负数，会把「合计」越加越小。优先用 evt.duration（按完整时间戳算好的分钟数），
     *    没有该字段时才退回 end - start 并夹到非负。
     * 2) 分类 key 不在当前分类列表里的记录（分类被删、或旧缓存里的历史 key），
     *    统一落到 UNCATEGORIZED_KEY 桶里，绝不能让它们从统计里消失。
     */
    hours() {
      const result = {}
      this.displayCats.forEach(c => {
        result[c.key] = new Array(7).fill(0)
      })
      for (let dIdx = 0; dIdx < 7; dIdx++) {
        const dayEvents = this.events[dIdx] || []
        dayEvents.forEach(evt => {
          const value = this.eventHours(evt)
          const key = result[evt.cat] ? evt.cat : UNCATEGORIZED_KEY
          if (!result[key]) result[key] = new Array(7).fill(0)
          result[key][dIdx] += value
        })
      }
      return result
    },
    uncategorized() {
      return this.hours[UNCATEGORIZED_KEY] || new Array(7).fill(0)
    },
    hasUncategorized() {
      return this.uncategorized.some(v => v > 0)
    },
    /**
     * 每日合计 = 当天**全部记录**之和。
     * 刻意不写成「只累加 displayCats」—— 那样一旦出现未分类记录，
     * 合计就会小于全部记录，正是之前被反馈的问题。
     */
    dayTotals() {
      const totals = new Array(7).fill(0)
      const keys = Object.keys(this.hours)
      for (let d = 0; d < 7; d++) {
        keys.forEach(k => {
          totals[d] += this.hours[k][d]
        })
      }
      return totals
    },
    weekTotal() {
      return this.dayTotals.reduce((sum, v) => sum + v, 0)
    },
    tableRows() {
      const rows = this.displayCats.map(cat => {
        // 名称字段里可能带着图标前缀（'fa:book学习' / '🍕吃饭'），
        // 这里只取纯文本部分，分类列一律「色点 + 文字」展示，不再渲染图标
        const parsed = parseTagName(cat.label)
        return {
          label: parsed.text,
          color: cat.color,
          values: this.hours[cat.key],
          weekTotal: this.hours[cat.key].reduce((sum, v) => sum + v, 0),
          isTotal: false
        }
      })
      // 只有确实存在未分类记录时才多出一行，保证「各行之和 = 合计」
      if (this.hasUncategorized) {
        rows.push({
          label: '未分类',
          color: null,
          values: this.uncategorized,
          weekTotal: this.uncategorized.reduce((sum, v) => sum + v, 0),
          isTotal: false
        })
      }
      // 分类行按「周累计」从高到低排序。
      // Array.prototype.sort 是稳定排序，累计相同的分类会保持后端返回的原有顺序。
      // 「合计」行不参与排序，固定钉在最后。
      rows.sort((a, b) => b.weekTotal - a.weekTotal)
      rows.push({
        label: '合计',
        color: null,
        values: this.dayTotals,
        weekTotal: this.weekTotal,
        isTotal: true
      })
      return rows
    }
  },
  methods: {
    /** 单条记录时长（小时），跨午夜安全、且不会返回负数 */
    eventHours(evt) {
      if (typeof evt.duration === 'number') {
        return evt.duration > 0 ? evt.duration / 60 : 0
      }
      const diff = (evt.end - evt.start) / 60
      return diff > 0 ? diff : 0
    },
    formatDuration(v) {
      if (!v || v <= 0) return '-'
      const totalMinutes = Math.round(v * 60)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      const parts = []
      if (hours > 0) parts.push(hours + 'h')
      if (minutes > 0) parts.push(minutes + 'm')
      return parts.join(' ') || '0m'
    },
    rowClassName({ rowIndex }) {
      const row = this.tableRows[rowIndex]
      return row && row.isTotal ? 'total-row' : ''
    }
  }
}
</script>

<style scoped>
.stats-wrap{
  margin-top:0.875rem;
}
.stats-table{
  width:100%;
  background:var(--lp-surface,#fff);
}
/* 分类（周累计）表外框：与时间表外框保持一致 —— 2px + 主色描边 + 同圆角，
   内部分隔线仍走极淡的网格（见下方 .stats-table >>> td/th 规则），形成"粗外框 + 细内线"的层次 */
.stats-frame{
  border:2px solid var(--lp-border-strong,#a5cdb3);
  border-radius:0.75rem;
  overflow:hidden;
}
/* 轻量扁平：网格线一律压到极淡的绿灰，去掉原来的橄榄绿表头 */
.stats-table >>> .el-table__body td,
.stats-table >>> .el-table__header th,
.stats-table >>> td,
.stats-table >>> th{
  border-color:var(--lp-border-soft,#eef5f0) !important;
}
.stats-table >>> thead th{
  background:var(--lp-surface-2,#f7fbf8) !important;
  color:var(--lp-text-2,#5f7268) !important;
  font-weight:600 !important;
  border-bottom:1px solid var(--lp-border,#e3efe6) !important;
}
.stats-table >>> .total-row td{
  background:var(--lp-surface-2,#f7fbf8) !important;
  border-top:1px solid var(--lp-primary,#4aa46a) !important;
}
/* 分类色点：与图例保持一致的**圆角方形**（不是圆点） */
.dot{
  width:10px;
  height:10px;
  border-radius:3px;
  display:inline-block;
  margin-right:0.375rem;
  border:1px solid rgba(0,0,0,.06);
  vertical-align:middle;
}
.total-label{
  font-weight:700;
  color:var(--lp-primary-dark,#2f7a4a);
}
.total-cell{
  color:var(--lp-primary,#4aa46a);
  font-weight:700;
}
.week-total{
  font-size:0.75rem;
  color:var(--lp-text-3,#94a59b);
}
.summary{
  margin-top:0.625rem;
  font-size:0.75rem;
  color:var(--lp-text-3,#94a59b);
  text-align:right;
}
.summary b{
  color:var(--lp-primary,#4aa46a);
}
/* Element 默认灰蓝收口：表头已在上面覆盖过，这里补正文文字与容器描边。
   注意 el-table 的根节点本身就带 .stats-table（同一个元素，不是后代），
   所以容器样式直接写 .stats-table，正文必须落到 td / .cell 上。 */
.stats-table{
  border-color:var(--lp-border-soft,#eef5f0);
}
.stats-table::before{
  background-color:var(--lp-border-soft,#eef5f0);
}
.stats-table >>> .el-table__body td,
.stats-table >>> .el-table__body .cell{
  color:var(--lp-text-2,#5f7268);
}
</style>